import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, ChevronDownIcon, ClockIcon, CopyIcon, CreditCardIcon, MapPinIcon, TruckIcon } from 'lucide-react';
import { AccountOrder } from '../../types';
import { ACCOUNT_STATUS_META } from '../../data/account';
import { formatPrice } from '../../utils/format';
import { useCart } from '../../contexts/CartContext';
import { PRODUCTS } from '../../data/products';
interface OrderCardProps {
  order: AccountOrder;
  defaultExpanded?: boolean;
}
export function OrderCard({
  order,
  defaultExpanded = false
}: OrderCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const {
    addItem
  } = useCart();
  const navigate = useNavigate();
  const meta = ACCOUNT_STATUS_META[order.status];
  const reorder = () => {
    order.items.forEach(item => {
      const product = PRODUCTS.find(p => p.name === item.name);
      if (product) {
        addItem(product.id, product.colors[0]?.id ?? 'black', item.quantity);
      }
    });
    navigate('/productos');
  };
  const copyTracking = () => {
    if (!order.tracking) return;
    navigator.clipboard?.writeText(order.tracking);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return <article className="border border-white/10 bg-ink-800" data-id="element-44" data-mp="2gv91mymxxlsk">
      <div className="flex flex-col gap-6 p-5 lg:flex-row lg:items-center" data-id="element-45" data-mp="2gwk2oqp04ffz">
        {/* LEFT */}
        <div className="lg:w-64" data-id="element-46" data-mp="1k6t8kskmguap">
          <p className="font-display text-xl leading-none tracking-wide text-pink-500" data-id="element-47" data-mp="1p31j1snvjzbj">
            {order.id}
          </p>
          <p className="mt-1.5 text-xs text-white/40" data-id="element-48" data-mp="1t4dc685yj2oh">{order.date}</p>
          <div className="mt-4 flex" data-id="element-49" data-mp="1y4r9av60ss7q">
            {order.items.slice(0, 3).map((item, i) => <img key={item.name} src={item.image} alt={item.name} className="h-12 w-12 border border-ink-800 object-cover ring-1 ring-white/10" style={{
            marginLeft: i === 0 ? 0 : -14,
            zIndex: 3 - i
          }} data-id="element-50" data-mp="34ktjfe7cyd09" data-mpi={i} />)}
          </div>
        </div>

        {/* CENTER */}
        <div className="min-w-0 flex-1" data-id="element-51" data-mp="15hmoqu7usj2x">
          <h3 className="text-base font-semibold text-white" data-id="element-52" data-mp="1jfl1xwafu9gu">{order.summary}</h3>
          <p className="mt-1 text-xs text-white/40" data-id="element-53" data-mp="1eghw49mmvz7t">
            {order.itemCount}{' '}
            {order.itemCount === 1 ? 'producto' : 'productos'}
          </p>
          <span className={`mt-3 inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${meta.badge}`} data-id="element-54" data-mp="qqxebu27v804">
            {meta.label}
          </span>

          {order.note && <p className="mt-3 text-xs text-white/45" data-id="element-55" data-mp="10ia0q658x5yr">{order.note}</p>}

          {order.status === 'enviado' && <div className="mt-5 max-w-md" data-id="element-56" data-mp="7zmkb4172f40">
              <div className="flex items-center" data-id="element-57" data-mp="7ybktges7tip">
                {order.timeline.map((step, i) => <React.Fragment key={step.label} data-id="element-58" data-mp="30tb3nsxxa9n" data-mpi={i}>
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${step.done ? 'bg-teal-500 text-black' : 'bg-ink-600 text-white/40'}`} aria-hidden data-id="element-59" data-mp="2eo6hegiz603z">
                      {step.done ? <CheckIcon className="h-3.5 w-3.5" data-id="element-60" data-mp="29plh6h3q3gfj" /> : <ClockIcon className="h-3.5 w-3.5" data-id="element-61" data-mp="2xffsuvcb2yy7" />}
                    </span>
                    {i < order.timeline.length - 1 && <span className={`h-[2px] flex-1 ${order.timeline[i + 1].done ? 'bg-teal-500' : 'bg-ink-600'}`} data-id="element-62" data-mp="2of157pbvd7rj" />}
                  </React.Fragment>)}
              </div>
              <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.1em] text-white/40" data-id="element-63" data-mp="1hz78czakguaq">
                <span data-id="element-64" data-mp="1x8kodq5cqk48">Recibido</span>
                <span data-id="element-65" data-mp="1x9ngg1eeuslq">Preparando</span>
                <span data-id="element-66" data-mp="1sgua593anc7j">Enviado</span>
                <span data-id="element-67" data-mp="1sfj4t4u79web">Entregado</span>
              </div>
            </div>}
        </div>

        {/* RIGHT */}
        <div className="flex flex-col items-stretch gap-2.5 lg:w-52" data-id="element-68" data-mp="1nfn35c8z6h1x">
          <p className="font-display text-3xl leading-none text-white lg:text-right" data-id="element-69" data-mp="1jhz4by45w44r">
            {formatPrice(order.total)}
          </p>
          <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className="flex items-center justify-center gap-2 border border-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white hover:text-black" data-id="element-70" data-mp="2b7ke714lp51z">
            Ver detalle
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-71" data-mp="vs4u7ob88to7" />
          </button>
          {order.status === 'entregado' && <button type="button" onClick={reorder} className="bg-pink-500 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-72" data-mp="37dx8oz6c0eod">
              Volver a comprar
            </button>}
          {order.status === 'enviado' && <button type="button" className="flex items-center justify-center gap-2 bg-pink-500 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-73" data-mp="3l4sbbtq8lzji">
              <TruckIcon className="h-3.5 w-3.5" data-id="element-74" data-mp="2zyofaf2qqe8" />
              Rastrear pedido
            </button>}
        </div>
      </div>

      {/* EXPANDED DETAIL */}
      {expanded && <div className="border-t border-white/10 bg-ink-900/60 p-5" data-id="element-75" data-mp="20s2yera9mud5">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-76" data-mp="24rtw7ey7mar6">
            Productos del pedido
          </h4>
          <ul className="mt-4 divide-y divide-white/5" data-id="element-77" data-mp="3334pkrd9287s">
            {order.items.map(item => <li key={item.name} className="flex items-center gap-4 py-3" data-id="element-78" data-mp="3h2xi8s8c6yie">
                <img src={item.image} alt={item.name} className="h-14 w-14 shrink-0 border border-white/10 object-cover" data-id="element-79" data-mp="3h0t83sjuh39o" />
                <div className="min-w-0 flex-1" data-id="element-80" data-mp="3qz0ly2iu757g">
                  <p className="truncate text-sm font-semibold text-white" data-id="element-81" data-mp="3m2sdf7z5aa99">
                    {item.name}
                  </p>
                  <p className="text-xs text-white/40" data-id="element-82" data-mp="4kzdxva8nkmm">Color: {item.color}</p>
                </div>
                <span className="text-xs text-white/45" data-id="element-83" data-mp="3vsbxggylga5p">×{item.quantity}</span>
                <span className="w-24 text-right text-sm text-white" data-id="element-84" data-mp="dn8noixon9yy">
                  {formatPrice(item.price * item.quantity)}
                </span>
              </li>)}
          </ul>

          <div className="mt-6 grid gap-6 border-t border-white/10 pt-6 sm:grid-cols-2" data-id="element-85" data-mp="s9xmeik41j4a">
            <div data-id="element-86" data-mp="x88wmuebhvn1">
              <h4 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-87" data-mp="x6o1p3o3caon">
                <MapPinIcon className="h-3.5 w-3.5 text-pink-500" data-id="element-88" data-mp="j7y3tltg66z8" />
                Dirección de envío
              </h4>
              <p className="mt-2.5 text-sm leading-relaxed text-white/50" data-id="element-89" data-mp="nbnq24mngch3">
                {order.address}
              </p>
            </div>
            <div data-id="element-90" data-mp="ea6bwrssykv1">
              <h4 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-91" data-mp="eapijcd05mtr">
                <CreditCardIcon className="h-3.5 w-3.5 text-pink-500" data-id="element-92" data-mp="3vnjj5lndea8g" />
                Método de pago
              </h4>
              <p className="mt-2.5 text-sm text-white/50" data-id="element-93" data-mp="3moy3pzd73bbw">{order.payment}</p>

              {order.tracking && <div className="mt-5" data-id="element-94" data-mp="3rnrgfpknqyb0">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-95" data-mp="3rmpyr294zdkb">
                    N° de seguimiento
                  </h4>
                  <div className="mt-2.5 flex items-center gap-2" data-id="element-96" data-mp="61tjk0au0xhk">
                    <span className="border border-white/10 bg-ink-800 px-3 py-2 font-mono text-xs text-white" data-id="element-97" data-mp="5zh4pw0df38q">
                      {order.tracking}
                    </span>
                    <button type="button" onClick={copyTracking} aria-label="Copiar número de seguimiento" className="border border-white/15 p-2 text-white/60 transition-colors hover:border-pink-500 hover:text-pink-500" data-id="element-98" data-mp="3t5q2xfnwt4j7">
                      {copied ? <CheckIcon className="h-3.5 w-3.5 text-teal-400" data-id="element-99" data-mp="39j1xi92fvd70" /> : <CopyIcon className="h-3.5 w-3.5" data-id="element-100" data-mp="35gymfljomam9" />}
                    </button>
                  </div>
                </div>}
            </div>
          </div>

          <div className="mt-6 border-t border-white/10 pt-6" data-id="element-101" data-mp="1fyi5dv1bq1u1">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-102" data-mp="1kymprw5crgpj">
              Estado del pedido
            </h4>
            <ol className="mt-5 space-y-0" data-id="element-103" data-mp="1p1japtid7own">
              {order.timeline.map((step, i) => <li key={step.label} className="flex gap-4" data-id="element-104" data-mp="1ty9q0emcigh0" data-mpi={i}>
                  <div className="flex flex-col items-center" data-id="element-105" data-mp="x9bu2t2v0ci9">
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${step.done ? 'bg-teal-500 text-black' : 'border border-white/15 bg-ink-800 text-white/40'}`} aria-hidden data-id="element-106" data-mp="xawn2e9izne0">
                      {step.done ? <CheckIcon className="h-4 w-4" data-id="element-107" data-mp="dj0071qswoy7" /> : <ClockIcon className="h-3.5 w-3.5" data-id="element-108" data-mp="iicqjwgvse5j" />}
                    </span>
                    {i < order.timeline.length - 1 && <span className={`w-[2px] flex-1 ${order.timeline[i + 1].done ? 'bg-teal-500' : 'bg-white/10'}`} data-id="element-109" data-mp="2qpil7829gnav" />}
                  </div>
                  <div className={i < order.timeline.length - 1 ? 'pb-6' : ''} data-id="element-110" data-mp="230z88f89nmcg">
                    <p className={`text-sm font-semibold ${step.done ? 'text-white' : 'text-white/40'}`} data-id="element-111" data-mp="22z4ov5cn82ah">
                      {step.label}
                    </p>
                    <p className="mt-0.5 text-xs text-white/35" data-id="element-112" data-mp="2hqx29bsvn69k">{step.date}</p>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>}
    </article>;
}